<template>
  <div>
    <v-autocomplete
      ref="autocomplete"
      v-model="selected"
      :items="formattedProperties"
      item-value="id"
      item-text="searchText"
      label="Select Property"
      :return-object="true"
      @change="onChange"
      clearable
      outlined
      dense
      hide-details
      prepend-inner-icon="mdi-magnify"
      class="property-autocomplete"
      :search-input.sync="search"
      :filter="customFilter"
      no-data-text="No properties found"
      auto-select-first
      @click:clear="clearSelection"
    >
      <!-- Selected Value (in the input box) -->
      <template v-slot:selection="{ item }">
        <div class="d-flex flex-column py-1">
          <strong class="text-body-2">{{ item.displayName }}</strong>
          <span class="text-caption grey--text">
            {{ item.baseAddress }}
            <span v-if="item.selectedUnit" class="font-weight-bold ml-1">
              ({{ item.selectedUnit.unitName }})
            </span>
          </span>
        </div>
      </template>

      <!-- Dropdown Items with Clickable Units -->
      <template v-slot:item="{ item }">
        <v-list-item @click="selectProperty(item)">
          <v-list-item-content>
            <v-list-item-title class="font-weight-bold">
              {{ item.displayName }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-caption grey--text">
              {{ item.baseAddress }}
            </v-list-item-subtitle>

            <!-- Vacant Units - Sorted Numerically -->
            <div v-if="item.sortedVacantUnits && item.sortedVacantUnits.length > 0" class="mt-2">
              <div
                v-for="unit in item.sortedVacantUnits"
                :key="unit.id"
                class="pl-6 py-1 text-caption cursor-pointer unit-option"
                :class="{ 'font-weight-bold primary--text': isUnitSelected(item, unit) }"
                @click.stop="selectUnit(item, unit)"
              >
                Unit: {{ unit.unitName }} - Vacant
              </div>
            </div>
            <div v-else class="pl-6 text-caption grey--text mt-2">
              No vacant units
            </div>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
export default {
  props: {
    properties: {
      type: Array,
      required: true,
    },
    selectedProperty: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      selected: this.selectedProperty || null,
      search: '',
    };
  },
  computed: {
    formattedProperties() {
      return this.properties.map(property => {
        const parts = [
          property.displayName,
          property.address?.building,
          property.address?.street,
          property.address?.suburb,
          property.address?.city,
          property.address?.postalCode
        ].filter(Boolean);

        const baseAddress = parts.slice(1).join(', ');
        const searchText = parts.join(' ').toLowerCase();

        // Filter and sort vacant units numerically
        const vacantUnits = (property.units || [])
          .filter(u => !u.isOccupied);

        const sortedVacantUnits = [...vacantUnits].sort((a, b) => {
          const numA = parseInt(a.unitName, 10);
          const numB = parseInt(b.unitName, 10);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          return a.unitName.localeCompare(b.unitName);
        });

        const selectedUnit = this.selected?.id === property.id
          ? this.selected.selectedUnit
          : null;

        return {
          ...property,
          baseAddress,
          searchText,
          selectedUnit,
          sortedVacantUnits,
        };
      });
    },
  },
  watch: {
    selectedProperty: {
      handler(val) {
        this.selected = val || null;
      },
      immediate: true,
    },
  },
  methods: {
    customFilter(item, queryText) {
      const text = item.searchText;
      return text.includes(queryText.toLowerCase());
    },
    selectProperty(property) {
      this.selected = { ...property, selectedUnit: null };
      this.closeMenu();
      this.emitProperty();
    },
    selectUnit(property, unit) {
      this.selected = { ...property, selectedUnit: unit };
      this.closeMenu();
      this.emitProperty();
    },
    clearSelection() {
      this.selected = null;
      this.emitProperty();
    },
    onChange() {
      // This handles when user selects via keyboard (Enter) or clicks outside
      this.closeMenu();
    },
    closeMenu() {
      // Force close the dropdown menu
      if (this.$refs.autocomplete) {
        this.$refs.autocomplete.isMenuActive = false;
      }
    },
    isUnitSelected(property, unit) {
      return this.selected?.id === property.id && 
             this.selected?.selectedUnit?.id === unit.id;
    },
    emitProperty() {
      this.$emit('property-selected', this.selected);
    },
  },
};
</script>

<style scoped>
.property-autocomplete {
  max-width: 100%;
}

.unit-option {
  cursor: pointer;
  border-radius: 4px;
  padding: 4px 8px;
  transition: all 0.2s;
}

.unit-option:hover {
  background-color: #e3f2fd;
}

.cursor-pointer {
  cursor: pointer;
}

::v-deep .v-list-item {
  min-height: 64px !important;
}
</style>